import { useCallback, useEffect, useMemo, useState } from 'react';
import { dateValidatorByFormat, Field } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { PlanStatuses } from '@/modules/plan/plan.types';
import { getDateIsoString } from '@/utils/modifiers/getDateIsoString';

import { useCancelEmployerTerminationModalStore, useEmployerTerminationModalStore, useEmployerTerminationSuccessModalStore } from '../../store';
import { TerminationSteps } from '../../store/useEmployerTerminationModal.store';
import { useGetEmployerTerminationDataQuery } from '../queries/useGetEmployerTerminationData.query';
import { PlanForTerminationPayload, useTerminateEmployerQuery } from '../queries/useTerminateEmployer.query';

import { useEmployerTerminationPlanList } from './useEmployerTerminationPlanList';
import { usePlanCoveragesDates } from './usePlanCoveragesDates';
import { usePlansCoveragesDatesValidation } from './usePlansCoveragesDatesValidation';

const NOT_APPLICABLE = 'NOT_APPLICABLE';
const PLAN_STATUSES = [PlanStatuses.ACTIVE, PlanStatuses.PENDING_TERMINATION];

export const dateRangeValidator = (
  endDate?: string,
  startDate?: string,
  maxDate?: string,
) => {
  if (!endDate || !startDate) return false;

  if (dayjs(endDate).isBefore(dayjs(startDate))) return true;

  if (dayjs(endDate).isAfter(dayjs(maxDate))) return true;
};

const today = dayjs().format(DEFAULT_DATE_FORMAT);

interface FormValues {
  terminationDate?: string;
  hsaTransferOfAssets?: string;
}

interface Props {
  onCancel?: (value: boolean) => void;
}

export const useTerminateEmployerModal = ({ onCancel }: Props) => {
  const [isSubmit, setSubmit] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({});

  const [dateIsFilled, setDateIsFilled] = useState(false);
  const [hsaIsFilled, setHsaIsFilled] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [permanentLastDate, setPermanentLastDate] = useState<string | undefined>();

  const [isTerminationDateError, setIsTerminationDateError] = useState(false);

  const terminationDate = useEmployerTerminationModalStore((state) => state.terminationDate);
  const employerId = useEmployerTerminationModalStore((state) => state.employerId);
  const handleSetTerminationDate = useEmployerTerminationModalStore(
    (state) => state.handleSetTerminationDate
  );
  const handleSetStep = useEmployerTerminationModalStore((state) => state.handleSetStep);
  const step = useEmployerTerminationModalStore((state) => state.step);
  const handleChangeSuccessVisibility = useEmployerTerminationSuccessModalStore(
    (state) => state.handleChangeVisibility
  );
  const handleChangeVisibility = useEmployerTerminationModalStore((state) => state.handleChangeVisibility);
  const editMode = useEmployerTerminationModalStore((state) => state.editMode);

  const handleChangeCancelVisibility = useCancelEmployerTerminationModalStore(
    (state) => state.handleChangeVisibility
  );

  const {
    planCoveragesErrors,
    getPlanCoveragesClaimDateError,
    getPlanCoveragesEndDateError,
    coverageErrorsParser,
  } = usePlansCoveragesDatesValidation();

  const {
    plansCoveragesDates,
    getPlanCoveragesClaimDate,
    getPlanCoveragesEndDate,
    setPlanCoveragesEndDate,
    setPlanCoveragesClaimDate,
    setPlansCoveragesDates,
  } = usePlanCoveragesDates();

  const isThereTerminationDate = useMemo(
    () => terminationDate || dateValidatorByFormat(formValues.terminationDate, DEFAULT_DATE_FORMAT),
    [formValues.terminationDate, terminationDate]
  );

  const {
    terminateEmployer,
    updateEmployerTermination,
    isLoading: terminationLoading,
  } = useTerminateEmployerQuery(employerId || '');
  const { terminationData, isLoading: terminationDataLoading } = useGetEmployerTerminationDataQuery(
    editMode ? employerId || '' : undefined
  );

  const currentTerminationDate = useMemo(() => {
    if (!!permanentLastDate && dayjs(formValues.terminationDate).isBefore(dayjs(permanentLastDate)))
      return undefined;
    return formValues.terminationDate || terminationDate || terminationData?.terminationDate;
  }, [formValues, terminationData, terminationDate, permanentLastDate]);

  const {
    isLoading: planListLoading,
    data: plansData,
    coverageData,
    isHsaAccountExist,
    hsaStartDate,
    currentCoverageData,
    currentValidFromDates,
    hsaPlanIds,
    hsaTransferOfAssets,
  } = useEmployerTerminationPlanList({
    orgId: employerId,
    terminationDate: isThereTerminationDate ? currentTerminationDate : today,
    isSubmitted: isSubmit,
    endOfCoverage: getPlanCoveragesEndDate,
    lastDayToSubmitClaim: getPlanCoveragesClaimDate,
    setEndOfCoverage: setPlanCoveragesEndDate,
    setLastDayToSubmitClaim: setPlanCoveragesClaimDate,
    isEndOfCoverageError: getPlanCoveragesEndDateError,
    isClaimLastDateError: getPlanCoveragesClaimDateError,
    statuses: PLAN_STATUSES,
  });

  const modalDescription = useMemo(() => {
    return `Please note that you can edit or cancel termination configurations until ${
      terminationDate || formValues.terminationDate
    }.`;
  }, [formValues.terminationDate, terminationDate]);

  const changeValues = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values: any,
      fields: Field[],
      setFieldsAreFilled?: (val: boolean) => void
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFormValues((prev: any) => ({ ...prev, ...values }));
      try {
        const validatorMap = fields.reduce(
          (map, field) => ({
            ...map,
            [field.name]: field.validator,
          }),
          {}
        );
        const schema = yup.object().shape(validatorMap);
        schema.validateSync(values, { abortEarly: false });
        if (setFieldsAreFilled) setFieldsAreFilled(true);
      } catch {
        if (setFieldsAreFilled) setFieldsAreFilled(false);
      }
    },
    [setFormValues]
  );

  const handleConfirmFirstStep = useCallback(() => {
    handleSetTerminationDate(formValues.terminationDate);
    handleSetStep(TerminationSteps.SET_TERMINATION_PARAMS);
  }, [formValues.terminationDate, handleSetStep, handleSetTerminationDate]);

  const plansPayloadConverter = useCallback((): PlanForTerminationPayload[] => {
    return plansCoveragesDates.map((plan) => {
      const isHsaPlan = hsaPlanIds.includes(plan.planId);
      return {
        plan_id: plan.planId,
        coverage_end_date: plan.coverageEndDate
          ? getDateIsoString(plan.coverageEndDate)
          : undefined,
        claim_last_date: plan.lastDayToSubmitClaim
          ? getDateIsoString(plan.lastDayToSubmitClaim)
          : undefined,
        hsa_transfer_of_assets: isHsaPlan
          ? formValues.hsaTransferOfAssets
          : NOT_APPLICABLE,
      };
    });
  }, [plansCoveragesDates, hsaPlanIds, formValues.hsaTransferOfAssets]);

  const handleSubmit = useCallback(async () => {
    setSubmit(true);

    if (!dateIsFilled) {
      return;
    }

    const isTerminationDateOutOfRange = dateRangeValidator(
      formValues?.terminationDate,
      permanentLastDate,
      dayjs().add(6, 'month').format(DEFAULT_DATE_FORMAT)
    );

    if (isTerminationDateOutOfRange) {
      setIsTerminationDateError(isTerminationDateOutOfRange);
      return;
    }

    if (step === TerminationSteps.SET_TERMINATION_DATE && plansData.length !== 0) {
      handleConfirmFirstStep();
      setSubmit(false);
      return;
    }

    const isAnyErrors = coverageErrorsParser(plansCoveragesDates, currentValidFromDates);
    if (isHsaAccountExist && !hsaIsFilled && step === TerminationSteps.SET_TERMINATION_PARAMS) {
      return;
    }

    if (isAnyErrors) {
      return;
    }

    try {
      handleSetTerminationDate(formValues.terminationDate);

      const payload = {
        termination_date: getDateIsoString(formValues.terminationDate) as string,
        plans_for_termination: plansPayloadConverter(),
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      editMode ? await updateEmployerTermination(payload) : await terminateEmployer(payload);
      handleChangeVisibility(false);
      handleChangeSuccessVisibility(true);
    } catch {
      setIsServerError(true);
    }
  }, [
    dateIsFilled,
    formValues,
    handleConfirmFirstStep,
    step,
    isHsaAccountExist,
    hsaIsFilled,
    handleChangeSuccessVisibility,
    handleChangeVisibility,
    terminateEmployer,
    plansData,
    handleSetTerminationDate,
    editMode,
    updateEmployerTermination,
    permanentLastDate,
    coverageErrorsParser,
    plansCoveragesDates,
    currentValidFromDates,
    plansPayloadConverter,
  ]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
    }
  }, [onCancel]);

  const handleCancelTermination = useCallback(() => {
    handleChangeVisibility(false);
    handleChangeCancelVisibility(true);
    handleSetStep(TerminationSteps.SET_TERMINATION_DATE);
  }, [handleChangeVisibility, handleChangeCancelVisibility, handleSetStep]);

  const currentButtonTitle = useMemo(() => {
    if (plansData.length === 0) {
      return 'Confirm';
    }
    if (step === TerminationSteps.SET_TERMINATION_DATE) {
      return 'Next (1/2)';
    }
    return 'Confirm (2/2)';
  }, [step, plansData]);

  useEffect(() => {
    if (!planListLoading) {
      setInitialLoading(false);
    }
  }, [planListLoading]);

  useEffect(() => {
    if (hsaStartDate && !permanentLastDate) {
      setPermanentLastDate(hsaStartDate);
    }
  }, [hsaStartDate, permanentLastDate]);

    useEffect(() => {
    if (formValues?.terminationDate && isSubmit) {
      const isTerminationDateOutOfRange = dateRangeValidator(
        formValues?.terminationDate,
        permanentLastDate,
        dayjs().add(6, 'month').format(DEFAULT_DATE_FORMAT)
      );
      if (isTerminationDateOutOfRange) {
        setIsTerminationDateError(isTerminationDateOutOfRange);
      } else {
        setIsTerminationDateError(false);
      }
    }
  }, [formValues?.terminationDate, isSubmit, permanentLastDate, terminationDate]);

  useEffect(() => {
    if (currentCoverageData) {
      const formattedData = currentCoverageData.map((item) => ({
        planId: item.id,
        coverageEndDate: item.coverageEndDate ? item.coverageEndDate : item.claimLastDate as string,
        lastDayToSubmitClaim: item.claimLastDate as string,
      }));
      setPlansCoveragesDates(formattedData);
    }
  }, [currentCoverageData, setPlansCoveragesDates]);

  useEffect(() => {
    if (plansCoveragesDates && isSubmit) {
      coverageErrorsParser(plansCoveragesDates, currentValidFromDates);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmit, plansCoveragesDates, currentValidFromDates]);

  return {
    isThereTerminationDate,
    modalDescription,
    terminationDataLoading,
    initialLoading,
    step,
    changeValues,
    isSubmit,
    setDateIsFilled,
    terminationDate,
    terminationData,
    permanentLastDate,
    planListLoading,
    plansData,
    coverageData,
    isHsaAccountExist,
    setHsaIsFilled,
    isTerminationDateError,
    editMode,
    handleCancelTermination,
    handleCancel,
    terminationLoading,
    handleSubmit,
    currentButtonTitle,
    isServerError,
    setIsServerError,
    planCoveragesErrors,
    formValues,
    hsaTransferOfAssets: editMode ? hsaTransferOfAssets : undefined,
  };
};
